"use client";

import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getPageTutorialSlides } from "@/components/tutorial/pageTutorialSlides";
import { TutorialSheet } from "@/components/tutorial/TutorialSheet";
import type { PageTutorialId } from "@/lib/storage";
import { hasPageTutorialBeenSeen, markPageTutorialAsSeen } from "@/lib/storage";

function detectPage(pathname: string): PageTutorialId | null {
  if (pathname.endsWith("/dashboard")) return "dashboard";
  if (/\/recipes(\/|$)/.test(pathname)) return "recipes";
  if (pathname.endsWith("/calendar")) return "calendar";
  return null;
}

export function PageTutorialManager() {
  const pathname = usePathname();
  const t = useTranslations("pageTutorial");
  const [activeTutorial, setActiveTutorial] = useState<PageTutorialId | null>(null);

  const currentPage = detectPage(pathname);

  useEffect(() => {
    if (!currentPage) return;
    if (hasPageTutorialBeenSeen(currentPage)) return;

    const timer = setTimeout(() => {
      setActiveTutorial(currentPage);
    }, 600);
    return () => clearTimeout(timer);
  }, [currentPage]);

  useEffect(() => {
    const handler = (e: Event) => {
      const page = (e as CustomEvent<PageTutorialId>).detail;
      setActiveTutorial(page);
    };
    window.addEventListener("open-page-tutorial", handler);
    return () => window.removeEventListener("open-page-tutorial", handler);
  }, []);

  const handleClose = useCallback(() => {
    if (activeTutorial) {
      markPageTutorialAsSeen(activeTutorial);
    }
    setActiveTutorial(null);
  }, [activeTutorial]);

  const slides = useMemo(() => {
    if (!activeTutorial) return [];
    return getPageTutorialSlides(activeTutorial, t);
  }, [activeTutorial, t]);

  const cachedSlidesRef = useRef(slides);
  if (slides.length > 0) {
    cachedSlidesRef.current = slides;
  }

  return (
    <TutorialSheet
      isOpen={activeTutorial !== null}
      onClose={handleClose}
      slides={activeTutorial !== null ? slides : cachedSlidesRef.current}
    />
  );
}
